import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { ForkKnife, ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const doLogin = async (em, pw) => {
    setBusy(true); setError("");
    try {
      const u = await login(em, pw);
      navigate(u.role === "developer" ? "/developer-admin" : ["staff", "supervisor"].includes(u.role) ? "/briefing" : "/dashboard");
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between gap-10 p-10 xl:p-14 bg-slate-900 text-white min-h-screen sticky top-0 h-screen overflow-hidden">
        <div className="flex items-center gap-3 shrink-0">
          <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <ForkKnife size={24} weight="duotone" className="text-emerald-400" />
          </div>
          <span className="font-heading font-extrabold text-xl tracking-tight">Practitality</span>
        </div>
        <div className="max-w-xl">
          <h1 className="font-heading text-4xl xl:text-5xl font-black tracking-tight leading-[1.1]">
            Every shift,<br />service-ready.<br />
            <span className="text-emerald-400">Proven with proof.</span>
          </h1>
          <p className="mt-6 text-slate-300 text-base xl:text-lg max-w-md leading-relaxed">
            SOPs, pre-shift readiness gates, timed floor tasks with photo proof, and AI-tagged guest reviews — one operational control loop.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400 shrink-0">
          <span className="flex items-center whitespace-nowrap"><span className="inline-block h-2 w-2 rounded-full bg-emerald-400 mr-2" />Ready</span>
          <span className="flex items-center whitespace-nowrap"><span className="inline-block h-2 w-2 rounded-full bg-amber-400 mr-2" />Pending</span>
          <span className="flex items-center whitespace-nowrap"><span className="inline-block h-2 w-2 rounded-full bg-red-400 mr-2" />Non-compliant</span>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-10 sm:px-10 sm:py-14 lg:px-12">
        <div className="w-full max-w-md rise-in">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
              <ForkKnife size={22} weight="duotone" className="text-emerald-400" />
            </div>
            <span className="font-heading font-extrabold text-xl tracking-tight">Practitality</span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Sign in to your venue</h2>
          <p className="text-slate-500 mt-2">Managers, staff and instructors use the same door.</p>

          <form className="mt-8 space-y-4" onSubmit={(e) => { e.preventDefault(); doLogin(email, password); }}>
            <input data-testid="login-username-input" type="text" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="Username" autoComplete="username"
              className="w-full h-14 px-5 rounded-xl border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-slate-900" />
            <div className="relative">
              <input data-testid="login-password-input" type={showPw ? "text" : "password"} required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Password" autoComplete="current-password"
                className="w-full h-14 px-5 pr-14 rounded-xl border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-slate-900" />
              <button type="button" data-testid="login-password-toggle" onClick={() => setShowPw(!showPw)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 tap-press">
                {showPw ? <EyeSlash size={22} /> : <Eye size={22} />}
              </button>
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</p>}
            <button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full h-14 rounded-full bg-slate-900 text-white font-heading font-bold text-base flex items-center justify-center gap-2 tap-press hover:bg-slate-800 disabled:opacity-50">
              {busy ? "Signing in…" : "Sign in"} <ArrowRight size={20} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
